import * as React from 'react';
import { useState } from 'react';
import {
    AppBar, Box, Menu, MenuItem, Toolbar, Typography, Button, Container,
    IconButton, Avatar, Tooltip, Drawer, List, ListItemButton,
    ListItemIcon, ListItemText, Divider, Stack,
} from '@mui/material';
import ArrowBackIosNewIcon    from '@mui/icons-material/ArrowBackIosNew';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import TwoWheelerIcon         from '@mui/icons-material/TwoWheeler';
import ExploreIcon            from '@mui/icons-material/Explore';
import DashboardIcon          from '@mui/icons-material/Dashboard';
import BarChartIcon           from '@mui/icons-material/BarChart';
import MapIcon                from '@mui/icons-material/Map';
import PeopleIcon             from '@mui/icons-material/People';
import LocationCityIcon       from '@mui/icons-material/LocationCity';
import MenuIcon               from '@mui/icons-material/Menu';
import CloseIcon              from '@mui/icons-material/Close';
import HomeIcon               from '@mui/icons-material/Home';
import LogoutIcon             from '@mui/icons-material/Logout';
import LoginIcon              from '@mui/icons-material/Login';
import PersonAddIcon          from '@mui/icons-material/PersonAdd';
import AddCircleOutlineIcon   from '@mui/icons-material/AddCircleOutline';
import DirectionsBikeIcon     from '@mui/icons-material/DirectionsBike';
import { useNavigate }    from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth }        from './context/AuthContext';
import { useCurrency }    from './context/CurrencyContext';
import LanguageSwitcher   from './components/LanguageSwitcher';
import logo from './assets/images/logo.png';

const CURRENCIES = [
    { code: 'EUR', symbol: '€',  name: 'Euro'             },
    { code: 'USD', symbol: '$',  name: 'US Dollar'        },
    { code: 'AMD', symbol: '֏',  name: 'Armenian Dram'    },
    { code: 'GBP', symbol: '£',  name: 'British Pound'    },
    { code: 'RUB', symbol: '₽',  name: 'Russian Ruble'    },
    { code: 'JPY', symbol: '¥',  name: 'Japanese Yen'     },
    { code: 'CNY', symbol: '¥',  name: 'Chinese Yuan'     },
    { code: 'AUD', symbol: 'A$', name: 'Australian Dollar'},
    { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar'  },
    { code: 'CHF', symbol: 'Fr', name: 'Swiss Franc'      },
];

function CurrencySwitcher() {
    const { currency, setCurrency } = useCurrency();
    const [anchor, setAnchor] = useState(null);
    const active = CURRENCIES.find(c => c.code === currency) || CURRENCIES[0];

    return (
        <>
            <Button onClick={e => setAnchor(e.currentTarget)} sx={{
                minWidth: 0, px: 1, color: 'var(--text-main)',
                fontWeight: 600, fontSize: '0.9rem', textTransform: 'none',
                '&:hover': { bgcolor: 'rgba(196,98,45,0.06)' },
                gap: 0.5, display: 'flex', alignItems: 'center',
            }}>
                <Typography variant="caption" sx={{ fontWeight: 800, fontSize: '1rem', lineHeight: 1 }}>
                    {active.symbol}
                </Typography>
                <Typography variant="caption" sx={{ ml: 0.3, fontWeight: 700, color: 'var(--text-secondary)' }}>
                    {active.code}
                </Typography>
            </Button>
            <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}
                  PaperProps={{ elevation: 3, sx: {
                      mt: 0.5, borderRadius: 2,
                      border: '1px solid rgba(196,98,45,0.12)',
                      minWidth: 180, overflow: 'hidden',
                  }}}
                  transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                  anchorOrigin={  { horizontal: 'right', vertical: 'bottom' }}>
                {CURRENCIES.map(cur => (
                    <MenuItem key={cur.code}
                              onClick={() => { setCurrency(cur.code); setAnchor(null); }}
                              selected={cur.code === currency}
                              sx={{
                                  py: 1, px: 2,
                                  fontWeight: cur.code === currency ? 700 : 400,
                                  color: cur.code === currency ? 'var(--primary-color)' : 'inherit',
                                  '&.Mui-selected': {
                                      bgcolor: 'rgba(196,98,45,0.06)',
                                      '&:hover': { bgcolor: 'rgba(196,98,45,0.10)' },
                                  },
                              }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                            <Typography sx={{ fontWeight: 700, minWidth: 22, textAlign: 'center' }}>
                                {cur.symbol}
                            </Typography>
                            <Box>
                                <Typography variant="body2" fontWeight="inherit">{cur.name}</Typography>
                                <Typography variant="caption" color="text.secondary">{cur.code}</Typography>
                            </Box>
                            {cur.code === currency && (
                                <Box sx={{ ml: 'auto', width: 6, height: 6, borderRadius: '50%',
                                    bgcolor: 'var(--primary-color)', flexShrink: 0 }} />
                            )}
                        </Box>
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
}

const navItemSx = {
    borderRadius: 2,
    mb: 0.5,
    '&:hover': { bgcolor: 'rgba(196,98,45,0.06)' },
};

export default function ButtonAppBar() {
    const { user }  = useAuth();
    const navigate  = useNavigate();
    const { t }     = useTranslation();
    const isManager = user?.role === 'MANAGER';
    const isAdmin   = user?.role === 'ADMIN';
    const isStaff   = isManager || isAdmin;
    const [drawerOpen, setDrawerOpen] = useState(false);


    return (
        <>
            {/* ── Side Drawer ──────────────────────────────────────────────── */}
            <Drawer anchor="left" open={drawerOpen} onClose={() => setDrawerOpen(false)}
                    PaperProps={{ sx: { width: 280, borderRadius: '0 16px 16px 0' } }}>
                <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: 'var(--card-bg)' }}>

                    {/* Header */}
                    <Box sx={{ px: 2.5, py: 2, display: 'flex', alignItems: 'center', gap: 1.5,
                               borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
                        <Box component="img" src={logo} alt="HayTour Logo"
                             sx={{ height: 32, width: 'auto', objectFit: 'contain' }} />
                        <Typography variant="h6" fontWeight={800}
                                    sx={{ color: 'var(--primary-color)', fontFamily: 'var(--font-family)', letterSpacing: '-0.5px' }}>
                            HAY<Box component="span" sx={{ color: 'var(--secondary-color)' }}>TOUR</Box>
                        </Typography>
                        <IconButton onClick={() => setDrawerOpen(false)} size="small" sx={{ ml: 'auto' }}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>

                    {/* Nav items */}
                    <List sx={{ px: 1, pt: 1.5, flex: 1, overflowY: 'auto' }}>

                        <ListItemButton component="a" href="/" onClick={() => setDrawerOpen(false)} sx={navItemSx}>
                            <ListItemIcon sx={{ minWidth: 40, color: 'var(--text-main)' }}><HomeIcon /></ListItemIcon>
                            <ListItemText primary="Home" primaryTypographyProps={{ fontWeight: 600 }} />
                        </ListItemButton>

                        <ListItemButton component="a" href="/tours" onClick={() => setDrawerOpen(false)} sx={navItemSx}>
                            <ListItemIcon sx={{ minWidth: 40, color: 'var(--text-main)' }}><ExploreIcon /></ListItemIcon>
                            <ListItemText primary="Browse Tours" primaryTypographyProps={{ fontWeight: 600 }} />
                        </ListItemButton>

                        <ListItemButton component="a" href="/rentals" onClick={() => setDrawerOpen(false)} sx={navItemSx}>
                            <ListItemIcon sx={{ minWidth: 40, color: 'var(--text-main)' }}><DirectionsBikeIcon /></ListItemIcon>
                            <ListItemText primary="Browse Rentals" primaryTypographyProps={{ fontWeight: 600 }} />
                        </ListItemButton>

                        {user && user.role !== 'GUIDE' && (
                            <ListItemButton component="a" href="/my-bookings" onClick={() => setDrawerOpen(false)} sx={navItemSx}>
                                <ListItemIcon sx={{ minWidth: 40, color: 'var(--text-main)' }}><ConfirmationNumberIcon /></ListItemIcon>
                                <ListItemText primary={t('nav.myBookings')}
                                              primaryTypographyProps={{ fontWeight: 600 }} />
                            </ListItemButton>
                        )}

                        {/* Guide section */}
                        {user?.role === 'GUIDE' && (
                            <>
                                <Divider sx={{ my: 1.5, mx: 1 }} />
                                <Typography variant="caption" color="text.secondary"
                                            sx={{ px: 2, pb: 0.5, display: 'block', textTransform: 'uppercase',
                                                  fontWeight: 700, letterSpacing: '0.08em', fontSize: '0.68rem' }}>
                                    My Schedule
                                </Typography>
                                <ListItemButton component="a" href="/guide/dashboard" onClick={() => setDrawerOpen(false)} sx={navItemSx}>
                                    <ListItemIcon sx={{ minWidth: 40, color: 'var(--text-main)' }}><BarChartIcon /></ListItemIcon>
                                    <ListItemText primary="My Dashboard" primaryTypographyProps={{ fontWeight: 600 }} />
                                </ListItemButton>
                                <ListItemButton component="a" href="/staff/tours" onClick={() => setDrawerOpen(false)} sx={navItemSx}>
                                    <ListItemIcon sx={{ minWidth: 40, color: 'var(--text-main)' }}><ExploreIcon /></ListItemIcon>
                                    <ListItemText primary="My Tours" primaryTypographyProps={{ fontWeight: 600 }} />
                                </ListItemButton>
                            </>
                        )}

                        {isStaff && (
                            <>
                                <Divider sx={{ my: 1.5, mx: 1 }} />
                                <Typography variant="caption" color="text.secondary"
                                            sx={{ px: 2, pb: 0.5, display: 'block', textTransform: 'uppercase',
                                                  fontWeight: 700, letterSpacing: '0.08em', fontSize: '0.68rem' }}>
                                    Management
                                </Typography>
                                <ListItemButton component="a" href="/dashboard" onClick={() => setDrawerOpen(false)} sx={navItemSx}>
                                    <ListItemIcon sx={{ minWidth: 40, color: 'var(--text-main)' }}><DashboardIcon /></ListItemIcon>
                                    <ListItemText primary={t('nav.dashboard')} primaryTypographyProps={{ fontWeight: 600 }} />
                                </ListItemButton>
                                <ListItemButton component="a" href="/staff/tours" onClick={() => setDrawerOpen(false)} sx={navItemSx}>
                                    <ListItemIcon sx={{ minWidth: 40, color: 'var(--text-main)' }}><ExploreIcon /></ListItemIcon>
                                    <ListItemText primary="Tours" primaryTypographyProps={{ fontWeight: 600 }} />
                                </ListItemButton>
                                <ListItemButton component="a" href="/staff/rentals" onClick={() => setDrawerOpen(false)} sx={navItemSx}>
                                    <ListItemIcon sx={{ minWidth: 40, color: 'var(--text-main)' }}><TwoWheelerIcon /></ListItemIcon>
                                    <ListItemText primary="Rentals" primaryTypographyProps={{ fontWeight: 600 }} />
                                </ListItemButton>
                                <ListItemButton component="a" href="/users" onClick={() => setDrawerOpen(false)} sx={navItemSx}>
                                    <ListItemIcon sx={{ minWidth: 40, color: 'var(--text-main)' }}><PeopleIcon /></ListItemIcon>
                                    <ListItemText primary={t('nav.users')} primaryTypographyProps={{ fontWeight: 600 }} />
                                </ListItemButton>
                                <ListItemButton component="a" href="/districts" onClick={() => setDrawerOpen(false)} sx={navItemSx}>
                                    <ListItemIcon sx={{ minWidth: 40, color: 'var(--text-main)' }}><LocationCityIcon /></ListItemIcon>
                                    <ListItemText primary={t('nav.districts', 'Districts')} primaryTypographyProps={{ fontWeight: 600 }} />
                                </ListItemButton>
                                <ListItemButton component="a" href="/district-ops" onClick={() => setDrawerOpen(false)} sx={navItemSx}>
                                    <ListItemIcon sx={{ minWidth: 40, color: 'var(--text-main)' }}><MapIcon /></ListItemIcon>
                                    <ListItemText primary="District Operations" primaryTypographyProps={{ fontWeight: 600 }} />
                                </ListItemButton>

                                <Divider sx={{ my: 1.5, mx: 1 }} />
                                <Typography variant="caption" color="text.secondary"
                                            sx={{ px: 2, pb: 0.5, display: 'block', textTransform: 'uppercase',
                                                  fontWeight: 700, letterSpacing: '0.08em', fontSize: '0.68rem' }}>
                                    Create
                                </Typography>
                                <ListItemButton component="a" href="/tours/create" onClick={() => setDrawerOpen(false)} sx={navItemSx}>
                                    <ListItemIcon sx={{ minWidth: 40, color: 'var(--primary-color)' }}><AddCircleOutlineIcon /></ListItemIcon>
                                    <ListItemText primary="Create Tour"
                                                  primaryTypographyProps={{ fontWeight: 600, color: 'var(--primary-color)' }} />
                                </ListItemButton>
                                <ListItemButton component="a" href="/rental-prices" onClick={() => setDrawerOpen(false)} sx={navItemSx}>
                                    <ListItemIcon sx={{ minWidth: 40, color: 'var(--primary-color)' }}><DirectionsBikeIcon /></ListItemIcon>
                                    <ListItemText primary="Rental Prices"
                                                  primaryTypographyProps={{ fontWeight: 600, color: 'var(--primary-color)' }} />
                                </ListItemButton>
                            </>
                        )}

                        <Divider sx={{ my: 1.5, mx: 1 }} />

                        {user ? (
                            <>
                                <ListItemButton component="a" href="/profile" onClick={() => setDrawerOpen(false)} sx={navItemSx}>
                                    <ListItemIcon sx={{ minWidth: 40 }}>
                                        <Avatar src={user.imageUrl} alt={user.username}
                                                sx={{ width: 26, height: 26, border: '1.5px solid var(--primary-color)' }} />
                                    </ListItemIcon>
                                    <ListItemText primary={t('nav.profile')} primaryTypographyProps={{ fontWeight: 600 }} />
                                </ListItemButton>
                                <ListItemButton component="a" href="/logout" onClick={() => setDrawerOpen(false)}
                                               sx={{ ...navItemSx, '&:hover': { bgcolor: 'rgba(211,47,47,0.06)' } }}>
                                    <ListItemIcon sx={{ minWidth: 40, color: 'error.main' }}><LogoutIcon /></ListItemIcon>
                                    <ListItemText primary={t('nav.logout')}
                                                  primaryTypographyProps={{ fontWeight: 600, color: 'error.main' }} />
                                </ListItemButton>
                            </>
                        ) : (
                            <>
                                <ListItemButton component="a" href="/login" onClick={() => setDrawerOpen(false)} sx={navItemSx}>
                                    <ListItemIcon sx={{ minWidth: 40, color: 'var(--text-main)' }}><LoginIcon /></ListItemIcon>
                                    <ListItemText primary={t('nav.login')} primaryTypographyProps={{ fontWeight: 600 }} />
                                </ListItemButton>
                                <ListItemButton component="a" href="/register" onClick={() => setDrawerOpen(false)} sx={navItemSx}>
                                    <ListItemIcon sx={{ minWidth: 40, color: 'var(--primary-color)' }}><PersonAddIcon /></ListItemIcon>
                                    <ListItemText primary={t('nav.signup')}
                                                  primaryTypographyProps={{ fontWeight: 600, color: 'var(--primary-color)' }} />
                                </ListItemButton>
                            </>
                        )}
                    </List>

                    {/* Bottom: language & currency */}
                    <Box sx={{ p: 2, borderTop: '1px solid rgba(0,0,0,0.08)' }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <LanguageSwitcher variant="icon" />
                            <CurrencySwitcher />
                        </Stack>
                    </Box>
                </Box>
            </Drawer>

            {/* ── App Bar ──────────────────────────────────────────────────── */}
            <AppBar position="sticky" elevation={0} sx={{
                backgroundColor: 'var(--card-bg)',
                borderBottom: '1px solid rgba(0,0,0,0.1)',
                backdropFilter: 'blur(8px)',
                zIndex: 1100,
                borderRadius: '0 !important',
            }}>
                <Container maxWidth="lg">
                    <Toolbar disableGutters>
                        {/* Hamburger — opens drawer on all screen sizes */}
                        <IconButton onClick={() => setDrawerOpen(true)} sx={{
                            mr: 1, color: 'var(--text-main)',
                            '&:hover': { backgroundColor: 'rgba(0,0,0,0.05)' },
                        }}>
                            <MenuIcon />
                        </IconButton>

                        {/* Back */}
                        <IconButton onClick={() => navigate(-1)} sx={{
                            mr: 2, color: 'var(--text-main)', transition: 'transform 0.2s',
                            '&:hover': { backgroundColor: 'rgba(0,0,0,0.05)', transform: 'translateX(-3px)' },
                        }}>
                            <ArrowBackIosNewIcon sx={{ fontSize: '1.2rem' }} />
                        </IconButton>

                        {/* Logo */}
                        <Box component="a" href="/" sx={{
                            display: 'flex', alignItems: 'center', flexGrow: 1, cursor: 'pointer', gap: 1.5,
                            textDecoration: 'none', color: 'inherit',
                        }}>
                            <Box component="img" src={logo} alt="HayTour Logo"
                                 sx={{ height: 40, width: 'auto', objectFit: 'contain' }} />
                            <Typography variant="h5" component="div"
                                        sx={{ fontWeight: 'bold', letterSpacing: '-1px', color: 'var(--primary-color)',
                                              fontFamily: 'var(--font-family)',
                                              display: { xs: 'none', sm: 'block' } }}>
                                HAY<Box component="span" sx={{ color: 'var(--secondary-color)' }}>TOUR</Box>
                            </Typography>
                        </Box>

                        {/* Browse links — visible on all sizes */}
                        <Box sx={{ display: { xs: 'none', sm: 'flex' }, gap: 0.5 }}>
                            <Button component="a" href="/tours" startIcon={<ExploreIcon />}
                                    sx={{ color: 'var(--text-main)', fontWeight: 600, textTransform: 'none' }}>
                                Tours
                            </Button>
                            <Button component="a" href="/rentals" startIcon={<DirectionsBikeIcon />}
                                    sx={{ color: '#4A6741', fontWeight: 700, textTransform: 'none',
                                          '&:hover': { bgcolor: 'rgba(74,103,65,0.08)' } }}>
                                Rentals
                            </Button>
                        </Box>

                        {/* Right-side controls */}
                        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                            <LanguageSwitcher variant="icon" />
                            <CurrencySwitcher />

                            {!user ? (
                                <>
                                    <Button component="a" href="/login"
                                            sx={{ color: 'var(--text-main)', fontWeight: 500,
                                                  display: { xs: 'none', sm: 'flex' } }}>
                                        {t('nav.login')}
                                    </Button>
                                    <Button component="a" href="/register" variant="contained"
                                            sx={{ backgroundColor: 'var(--primary-color)', color: '#fff',
                                                  borderRadius: 'var(--border-radius)', textTransform: 'none',
                                                  '&:hover': { backgroundColor: 'var(--primary-color)', opacity: 0.9 } }}>
                                        {t('nav.signup')}
                                    </Button>
                                </>
                            ) : (
                                <>
                                    {user.role !== 'GUIDE' && (
                                        <Button component="a" href="/my-bookings" startIcon={<ConfirmationNumberIcon />}
                                                sx={{ color: 'var(--text-main)', fontWeight: 600,
                                                      textTransform: 'none', display: { xs: 'none', sm: 'flex' } }}>
                                            {t('nav.myBookings')}
                                        </Button>
                                    )}
                                    <Tooltip title={t('nav.profile')}>
                                        <IconButton component="a" href="/profile" sx={{ p: 0 }}>
                                            <Avatar src={user.imageUrl} alt={user.username}
                                                    sx={{ width: 40, height: 40,
                                                          border: '2px solid var(--primary-color)',
                                                          transition: '0.2s', '&:hover': { opacity: 0.8 } }} />
                                        </IconButton>
                                    </Tooltip>
                                    <Button component="a" href="/logout" variant="outlined"
                                            sx={{ color: 'var(--primary-color)', borderColor: 'var(--primary-color)',
                                                  borderRadius: 'var(--border-radius)', textTransform: 'none',
                                                  fontWeight: 'bold', display: { xs: 'none', sm: 'flex' } }}>
                                        {t('nav.logout')}
                                    </Button>
                                </>
                            )}
                        </Box>
                    </Toolbar>
                </Container>
            </AppBar>
        </>
    );
}
